
import type { GridRow, GridColumn, FilterGroup, FilterCondition } from '../types/gridTypes';

export const getOperatorsForType = (type: string): { value: string; label: string }[] => {
  switch (type) {
    case 'number':
    case 'currency':
    case 'percent':
      return [
        { value: 'equals', label: '=' },
        { value: 'not_equals', label: '!=' },
        { value: 'gt', label: '>' },
        { value: 'gte', label: '>=' },
        { value: 'lt', label: '<' },
        { value: 'lte', label: '<=' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
      ];
    case 'date':
      return [
        { value: 'date_is', label: 'Is' },
        { value: 'date_before', label: 'Before' },
        { value: 'date_after', label: 'After' },
        { value: 'date_between', label: 'Between' },  // Story 4.3: Date range filter
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
      ];
    case 'select':
    case 'status':
      return [
        { value: 'equals', label: 'Is' },
        { value: 'not_equals', label: 'Is not' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
      ];
    default:
      return [
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does not contain' },
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Does not equal' },
        { value: 'starts_with', label: 'Starts with' },
        { value: 'ends_with', label: 'Ends with' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
      ];
  }
};

export const checkCondition = (value: any, condition: FilterCondition, columnType: string): boolean => {
  const { operator, value: filterValue } = condition;

  // Handle Empty Checks
  if (operator === 'is_empty') return value === null || value === undefined || value === '';
  if (operator === 'is_not_empty') return value !== null && value !== undefined && value !== '';

  // Standardize Value
  let currentVal = value;
  let targetVal = filterValue;

  // Logic per Type
  if (columnType === 'number' || columnType === 'currency' || columnType === 'percent') {
    currentVal = parseFloat(currentVal);
    targetVal = parseFloat(targetVal);
    
    if (isNaN(currentVal)) return false; // If cell is not a number, it fails numeric comparisons
    if (isNaN(targetVal)) return true; // If filter is invalid, maybe ignore?

    switch (operator) {
      case 'equals': return currentVal === targetVal;
      case 'not_equals': return currentVal !== targetVal;
      case 'gt': return currentVal > targetVal;
      case 'gte': return currentVal >= targetVal;
      case 'lt': return currentVal < targetVal;
      case 'lte': return currentVal <= targetVal;
      default: return false;
    }
  }

  if (columnType === 'date') {
      // Assuming value is timestamp number or date string
      const dateVal = new Date(currentVal).getTime();

      // Story 4.3: Handle date_between with [start, end] array
      if (operator === 'date_between' && Array.isArray(targetVal)) {
        const [startStr, endStr] = targetVal;
        const startDate = startStr ? new Date(startStr).getTime() : -Infinity;
        // Add end of day to endDate for inclusive range
        const endDate = endStr ? new Date(endStr + 'T23:59:59.999').getTime() : Infinity;

        if (isNaN(dateVal)) return false;
        return dateVal >= startDate && dateVal <= endDate;
      }

      // If filterValue is "YYYY-MM-DD" from input date
      const filterDate = new Date(targetVal).getTime();

      if (isNaN(dateVal)) return false;
      if (isNaN(filterDate)) return true;

      switch(operator) {
          case 'date_is':
            // Compare only YYYY-MM-DD
            return new Date(dateVal).toDateString() === new Date(filterDate).toDateString();
          case 'date_before': return dateVal < filterDate;
          case 'date_after': return dateVal > filterDate;
          default: return false;
      }
  }

  // String / Default
  const strVal = String(currentVal ?? '').toLowerCase();
  const strFilter = String(targetVal ?? '').toLowerCase();

  switch (operator) {
    case 'equals': return strVal === strFilter;
    case 'not_equals': return strVal !== strFilter;
    case 'contains': return strVal.includes(strFilter);
    case 'not_contains': return !strVal.includes(strFilter);
    case 'starts_with': return strVal.startsWith(strFilter);
    case 'ends_with': return strVal.endsWith(strFilter);
    default: return false;
  }
};

export const applyFilters = (
  rows: GridRow[],
  columns: GridColumn[],
  filterGroup?: FilterGroup
): GridRow[] => {
  if (!filterGroup || filterGroup.conditions.length === 0) return rows;

  return rows.filter(row => {
    // AND logic
    return filterGroup.conditions.every(cond => {
      const col = columns.find(c => c.id === cond.columnId);
      if (!col) return true; // Ignore missing columns
      return checkCondition(row.values[col.id], cond, col.type);
    });
  });
};

export const applySearch = (
  rows: GridRow[],
  columns: GridColumn[],
  query: string
): GridRow[] => {
  if (!query) return rows;
  const lowerQuery = query.toLowerCase();

  return rows.filter(row => {
    // Check all columns
    return columns.some(col => {
      const val = row.values[col.id];
      if (val === null || val === undefined) return false;
      return String(val).toLowerCase().includes(lowerQuery);
    });
  });
};
